import { Menu, RefreshCw, Plus, Calendar, Wallet, Megaphone, UserPlus, X, ChevronDown } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';

/**
 * Off-white minimal header (inspired by Arcads.ai).
 * Maroon is used ONLY as accent color for interactive elements (brand mark + FAB).
 * Includes a Floating Action Button / Quick-Add dropdown on the right.
 */
const QUICK_ACTIONS = [
  { id: 'add-staff',   label: 'Tambah Pekerja',   icon: UserPlus,  path: '/staff-payroll', hint: 'Buka borang staf baru', roles: ['director', 'finance', 'hr'] },
  { id: 'apply-leave', label: 'Mohon Cuti',       icon: Calendar,  path: '/me',            hint: 'Self-service portal staf', roles: ['staff', 'director', 'finance', 'hr', 'viewer'] },
  { id: 'add-claim',   label: 'Rekod Tuntutan',   icon: Wallet,    path: '/expense-claims',hint: 'Submit tuntutan belanja',  roles: ['director', 'finance', 'hr', 'staff', 'viewer'] },
  { id: 'announce',    label: 'Buat Pengumuman',  icon: Megaphone, path: '/announcements', hint: 'Siaran dalaman ke pasukan', roles: ['director', 'finance', 'hr'] },
];

const Header = () => {
  const { setSidebarOpen, fetchAllData, loading, userRole } = useApp();
  const [quickOpen, setQuickOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!dropdownRef.current?.contains(e.target)) setQuickOpen(false);
    };
    if (quickOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [quickOpen]);

  const effective = userRole === 'viewer' ? 'viewer' : userRole;
  const visibleActions = QUICK_ACTIONS.filter(a => !a.roles || a.roles.includes(effective));

  return (
    <header className="bg-white/95 backdrop-blur-md sticky top-0 z-30 border-b border-gray-200">
      <div className="px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Left: mobile menu + title */}
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200 lg:hidden"
              aria-label="Buka menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img src="/underads-logo.png" alt="UA Syarikat" className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg shadow-sm object-cover shrink-0" />
              <h1 className="text-base sm:text-lg font-bold text-gray-800 truncate tracking-tight">
                <span className="sm:hidden">UA Syarikat</span>
                <span className="hidden sm:inline">UA Syarikat <span className="text-gray-300 mx-1">·</span> <span className="text-gray-500 font-medium">Kewangan &amp; HR</span></span>
              </h1>
            </div>
          </div>

          {/* Right: Refresh + Quick Add */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700"
              aria-label="Refresh data"
              data-testid="header-refresh-btn"
              title="Muat semula"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>

            {visibleActions.length > 0 && (
              <div ref={dropdownRef} className="relative">
                <button
                  data-testid="quick-add-btn"
                  onClick={() => setQuickOpen(o => !o)}
                  className="flex items-center gap-1.5 pl-3 pr-3 sm:pr-2 min-h-[44px] bg-red-800 hover:bg-red-900 text-white rounded-full text-sm font-semibold shadow-sm transition-colors"
                  aria-label="Quick Add"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Tindakan Pantas</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${quickOpen ? 'rotate-180' : ''}`} />
                </button>

                {quickOpen && (
                  <div data-testid="quick-add-menu"
                    className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-[fadeIn_150ms_ease-out]">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                      <p className="text-xs tracking-wider uppercase font-bold text-gray-500">Tindakan Pantas</p>
                      <button onClick={() => setQuickOpen(false)} className="p-1 hover:bg-gray-100 rounded-full text-gray-400">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="py-1">
                      {visibleActions.map(a => (
                        <button
                          key={a.id}
                          data-testid={`quick-${a.id}`}
                          onClick={() => { setQuickOpen(false); navigate(a.path); }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left transition-colors"
                        >
                          <div className="w-9 h-9 rounded-full bg-red-50 text-red-700 flex items-center justify-center shrink-0">
                            <a.icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-800">{a.label}</p>
                            <p className="text-[11px] text-gray-500 truncate">{a.hint}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
